import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { SelectOption, SnackbarService } from '@bcl/design-system';
import { IonModal, Platform } from '@ionic/angular';
import {
  VisitService,
  TotalCatchesV2MobileModel,
  VisitAreasModulesModel,
} from 'src/app/services/visit.service';
import { MatDialog } from '@angular/material/dialog';
import { TotalCatchesDialogComponent } from '../total-catches-dialog/total-catches-dialog.component';
import {
  FollowUpAreasModulesDialogComponent,
  FollowUpAreasModulesDialogData,
  FollowUpAreasModulesDialogResult,
} from '../follow-up-areas-modules-dialog/follow-up-areas-modules-dialog.component';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { BaseAccordionComponent } from '../base-accordion.component';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
import { AccordionService } from 'src/app/services/accordion.service';
import { Monitor } from 'src/app/pages/area/area.model';

@Component({
  selector: 'app-visit-summary',
  templateUrl: './visit-summary.component.html',
  styleUrls: ['./visit-summary.component.scss'],
})
export class VisitSummaryComponent
  extends BaseAccordionComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() visit: any;
  @Input() scoreStatusLabels: SelectOption[] = [];
  @Input() backRoute = '';
  @Input() backUrl: string;
  @Input() isEditingDisabled = false;
  @Input() totalCatchesData: TotalCatchesV2MobileModel | null = null;
  @Input() isIPMQuestionnaireFilled: boolean | null = null;
  @Input() allMonitorsForVisit: Monitor[] = [];
  @Output() saveSummaryField = new EventEmitter<void>();
  @Output() saveScoreStatus = new EventEmitter();
  @Output() questionnaireRequiredChangesUpdated = new EventEmitter<void>();

  @ViewChild('signatureModal', { static: false }) signatureModal!: IonModal;

  public form: FormGroup;
  public signatureForm: FormGroup;
  public showFollowUp = false;
  public signature = '';
  public signaturePath: SafeHtml = '';
  public isSignatureEdit = false;
  public isSignatureModalOpen = false;
  public satisfied = true;
  public noContactPersonToSign = false;
  public scoreStatus: string | null = null;
  public dissatisfactionReason = '';
  public isLoading = false;
  public followUpWarning: string | null = null;
  public followUpOptions = [
    { value: true, label: 'MONITOR.DETAILS.OVERVIEW.LABELS.YES' },
    { value: false, label: 'MONITOR.DETAILS.OVERVIEW.LABELS.NO' },
  ];

  public ipmQuestionnaireUpdateOptions = [
    { value: true, label: 'MONITOR.DETAILS.OVERVIEW.LABELS.YES' },
    { value: false, label: 'MONITOR.DETAILS.OVERVIEW.LABELS.NO' },
  ];

  public initialMemo: string | null = null;
  public initialDissatisfactionReason: string | null = null;
  public initialScoreStatus: string | null = null;
  public initialTechnicianFollowUpRemarks: string | null = null;
  public isInitializing = true;
  public selectedAreasModules: VisitAreasModulesModel[] = [];
  public hasUnchangedCheckStatusMonitors = false;

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private domSanitizer: DomSanitizer,
    private visitService: VisitService,
    private router: Router,
    private snackbarService: SnackbarService,
    public platform: Platform,
    accordionService: AccordionService,
    private dialog: MatDialog
  ) {
    super(accordionService);
    const today = new Date().toISOString().split('T')[0];
    this.form = this.fb.group({
      visitStartDate: [today],
      visitEndDate: [today],
      signatureDate: [today],
      signatureName: [null],
      signatureFirstName: [null],
      signatureTitle: [null],
      signature: [null],
      scoreStatus: [null, Validators.required],
      satisfied: [true],
      noContactPersonToSign: [false],
      followUpNeeded: [null, [Validators.required]],
      followUpWeeksAhead: [null],
      followUpDuration: [null],
      memo: [null],
      dissatisfactionReason: [null],
      technicianFollowUpRemarks: [null],
      ipmQuestionnaireUpdateNeeded: [null, [Validators.required]],
    });

    this.signatureForm = this.fb.group({
      signature: [null, Validators.required],
    });
  }

  get isFormDisabled(): boolean {
    return this.isEditingDisabled;
  }

  get hasVerminModule(): boolean {
    return (
      this.visit?.modules?.some((module: any) => module.vermin === true) ??
      false
    );
  }

  get shouldShowIPM(): boolean {
    return (
      this.hasVerminModule &&
      this.visit?.customFormId !== null &&
      this.visit?.customFormId !== undefined &&
      this.visit?.customFormId !== ''
    );
  }

  get slideState() {
    return this.showItemList ? 'in' : 'out';
  }

  get arrowState() {
    return this.showItemList ? 'up' : 'down';
  }

  public get totalCatches(): number {
    return this.totalCatchesData?.totalCatches ?? 0;
  }

  public get maxNumberOfVisitsToAdd(): number | null {
    if (!this.visit?.visitMax) {
      return null;
    }
    if (this.visit.visitsCount && this.visit.visitMax) {
      return this.visit.visitMax - this.visit.visitsCount;
    }
    if (!this.visit.visitsCount && this.visit.visitMax) {
      return this.visit.visitMax;
    }
    return null;
  }

  public get isMaxVisitsReached(): boolean {
    const maxVisits = this.maxNumberOfVisitsToAdd;
    return maxVisits !== null && maxVisits <= 0;
  }

  public get shouldShowFollowUpQuestion(): boolean {
    return !this.isMaxVisitsReached;
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.scoreStatus = this.visit.scoreStatus?.toString();
    this.initialScoreStatus = this.visit.scoreStatus?.toString();
    this.initialMemo = this.visit.visitSummary ?? null;
    this.initialDissatisfactionReason = this.visit.satisfiedMemo ?? null;

    const today = new Date().toISOString().split('T')[0];
    this.form.patchValue({
      visitStartDate: this.visit.startedAt?.split('T')[0] ?? today,
      visitEndDate: this.visit.completedOn?.split('T')[0] ?? today,
      memo: this.visit.visitSummary ?? null,
      signatureDate: this.visit.signatureDate ?? today,
      signatureName: this.visit.signatureName ?? null,
      signatureFirstName: this.visit.signatureFirstName ?? null,
      signatureTitle: this.visit.signatureTitle ?? null,
      signature: this.visit.signature ?? null,
      scoreStatus: this.visit.scoreStatus?.toString() ?? null,
      satisfied: this.visit.satisfied ?? true,
      noContactPersonToSign: this.visit.noContactPersonToSign ?? false,
      dissatisfactionReason: this.visit.satisfiedMemo ?? '',
      ipmQuestionnaireUpdateNeeded:
        this.visit.questionnaireRequiredChanges === true && this.shouldShowIPM
          ? true
          : false,
    });

    this.isInitializing = false;

    if (
      !this.shouldShowIPM &&
      this.visit.questionnaireRequiredChanges !== false
    ) {
      this.visit.questionnaireRequiredChanges = false;
      this.questionnaireRequiredChangesUpdated.emit();
    }

    this.signatureForm.patchValue({
      signature: this.visit.signature,
    });
    this.signature = this.visit.signature;

    this.updateSignatureValidation(this.visit.noContactPersonToSign ?? false);
    this.updateFollowUpWeeksAheadValidation(this.visit.followUpNeeded ?? false);
    this.updateFollowUpValidation();

    this.updateFormDisabledState();
    this.updateIpmQuestionnaireValidation();
    this.checkMonitorsCheckStatus();

    this.form.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged((prev, curr) => {
          const { memo: prevMemo, scoreStatus: prevScore, ...prevRest } = prev;
          const { memo: currMemo, scoreStatus: currScore, ...currRest } = curr;
          return JSON.stringify(prevRest) === JSON.stringify(currRest);
        })
      )
      .subscribe(() => {
        if (!this.isInitializing) {
          this.saveSummaryField.emit();
          this.saveScoreStatus.emit();
        }
      });

    this.form.get('followUpNeeded')?.valueChanges.subscribe((value) => {
      this.showFollowUp = value;
      this.updateFollowUpWeeksAheadValidation(value);
    });

    this.form.get('followUpWeeksAhead')?.valueChanges.subscribe((value) => {
      if (value) {
        this.checkNextVisitDate(value);
      }
    });

    this.form.get('satisfied').valueChanges.subscribe((value) => {
      this.satisfied = value;
      if (value) {
        this.form.get('dissatisfactionReason').setValue('');
      } else {
        this.form.get('noContactPersonToSign').setValue(false);
      }
    });

    this.form.get('noContactPersonToSign').valueChanges.subscribe((value) => {
      this.noContactPersonToSign = value;
      this.updateSignatureValidation(value);
    });

    this.form
      .get('ipmQuestionnaireUpdateNeeded')
      ?.valueChanges.pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((value) => {
        if (!this.isInitializing && this.shouldShowIPM) {
          this.visit.questionnaireRequiredChanges = value;
          this.questionnaireRequiredChangesUpdated.emit();
        }
      });

    // Debounced memo changes - only save if actually changed and not during initialization
    this.form
      .get('memo')
      ?.valueChanges.pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((value) => {
        if (!this.isInitializing && value !== this.initialMemo) {
          this.visit.visitSummary = value;
          this.saveSummaryField.emit();
          this.saveScoreStatus.emit();
        }
      });

    this.form
      .get('signatureDate')
      ?.valueChanges.pipe(distinctUntilChanged())
      .subscribe((value) => {
        if (!this.isInitializing) {
          this.saveSignatureMetadata();
        }
      });

    this.form
      .get('signatureName')
      ?.valueChanges.pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((value) => {
        if (!this.isInitializing) {
          this.saveSignatureMetadata();
        }
      });

    this.form
      .get('signatureFirstName')
      ?.valueChanges.pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((value) => {
        if (!this.isInitializing) {
          this.saveSignatureMetadata();
        }
      });

    this.form
      .get('signatureTitle')
      ?.valueChanges.pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((value) => {
        if (!this.isInitializing) {
          this.saveSignatureMetadata();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isEditingDisabled) {
      const currentValue = changes.isEditingDisabled.currentValue;
      const previousValue = changes.isEditingDisabled.previousValue;

      if (currentValue !== previousValue) {
        this.updateFormDisabledState();
        this.updateIpmQuestionnaireValidation();
      }
    }

    if (changes.isIPMQuestionnaireFilled) {
      this.updateIpmQuestionnaireValidation();
    }

    if (changes.allMonitorsForVisit) {
      this.checkMonitorsCheckStatus();
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  public setMemo(desc: string) {
    this.form.get('memo')?.setValue(desc);
    this.form.markAsDirty();
  }

  public setTechnicianFollowUpRemarks(text: string) {
    this.form.get('technicianFollowUpRemarks')?.setValue(text);
    this.form.markAsDirty();
  }

  saveSignatureMetadata() {
    this.visit.signatureDate = this.form.value.signatureDate;
    this.visit.signatureName = this.form.value.signatureName;
    this.visit.signatureFirstName = this.form.value.signatureFirstName;
    this.visit.signatureTitle = this.form.value.signatureTitle;
    this.visitService
      .sendSignature(
        this.visit.visitId,
        this.signature || this.visit.signature,
        this.visit.signatureName,
        this.visit.signatureFirstName,
        this.visit.signatureTitle,
        this.visit.signatureDate
      )
      .subscribe((res) => {});
  }

  saveSignature() {
    this.signatureForm.get('signature')?.disable();
    this.isSignatureEdit = false;
    this.signature = this.signatureForm.value.signature;
    this.visit.signatureDate = this.form.value.signatureDate;
    this.visit.signatureName = this.form.value.signatureName;
    this.visit.signatureFirstName = this.form.value.signatureFirstName;
    this.visit.signatureTitle = this.form.value.signatureTitle;
    this.visitService
      .sendSignature(
        this.visit.visitId,
        this.signature,
        this.visit.signatureName,
        this.visit.signatureFirstName,
        this.visit.signatureTitle,
        this.visit.signatureDate
      )
      .subscribe((res) => {
        this.signaturePath = this.domSanitizer.bypassSecurityTrustHtml(
          this.signature
        );
        this.visit.signature = this.signature;
        this.closeSignatureModal();
        this.snackbarService.open(
          'green',
          'AREA.DETAILS.VISIT_SUMMARY.MESSAGES.SIGNATURE_SAVED'
        );
      });
  }

  changeScoreStatus(scoreStatus: string) {
    // Only save if the score status actually changed from its initial value
    if (scoreStatus !== this.initialScoreStatus) {
      this.form.get('scoreStatus').setValue(scoreStatus);
      this.form.markAsDirty();
      this.scoreStatus = scoreStatus;
      this.visit.scoreStatus = scoreStatus;
      this.saveScoreStatus.emit();
    } else {
      // Still update the visual state even if we don't save
      this.form.get('scoreStatus').setValue(scoreStatus);
      this.scoreStatus = scoreStatus;
      this.visit.scoreStatus = scoreStatus;
    }
  }

  endVisit() {
    this.isLoading = true;
    const formValue = this.form.value;

    if (formValue.noContactPersonToSign) {
      formValue.signatureDate = null;
      formValue.signatureName = null;
      formValue.signatureFirstName = null;
      formValue.signatureTitle = null;
      formValue.satisfied = null;
      formValue.dissatisfactionReason = null;
      this.signature = '';
    }

    const endVisitData = {
      ...formValue,
      signature: this.signature,
      visitId: this.visit.visitId,
      scoreStatus: this.scoreStatus,
      areasModulesList: this.selectedAreasModules,
      questionnaireRequiredChanges: this.shouldShowIPM
        ? this.visit.questionnaireRequiredChanges
        : false,
    };

    this.visitService.sendEndVisitDetails(endVisitData).subscribe({
      next: () => {
        this.visit.actualEndTime = new Date().toISOString();
        this.visit.timesheetEndTime = true;
        this.visit.visitStatus = 'Completed';
        this.visit.visitSummary = formValue.memo;
        this.visit.signatureDate = formValue.signatureDate;
        this.visit.signatureName = formValue.signatureName;
        this.visit.signatureFirstName = formValue.signatureFirstName;
        this.visit.signatureTitle = formValue.signatureTitle;
        this.visit.signature = this.signature;
        this.visit.scoreStatus = formValue.scoreStatus;
        this.visit.satisfied = formValue.satisfied;
        this.visit.satisfiedMemo = formValue.dissatisfactionReason;
        this.visit.completedOn = new Date(formValue.visitEndDate).toISOString();
        this.visit.startedAt = new Date(formValue.visitStartDate).toISOString();
        this.visit.isCompleted = true;
        this.visit.isReopened = false;
        this.visit.technicianStarted = false;
        this.isLoading = false;
        this.router.navigate([this.backRoute]);
      },
      error: () => {
        this.snackbarService.open(
          'red',
          'GENERIC_ERROR_MESSAGES.AN_ERROR_OCCURRED_PLEASE_TRY_AGAIN_LATER'
        );
        this.isLoading = false;
      },
    });
  }

  setDissatisfactionReason(text: string) {
    this.form.get('dissatisfactionReason').setValue(text);
    this.form.markAsDirty();
  }

  openIpmQuestionnaire() {
    this.saveSummaryField.emit();
    this.saveScoreStatus.emit();
    this.router.navigate(
      ['questionnaire', this.visit.customFormId, 'PCVisit', this.visit.id],
      {
        state: { backUrl: this.backUrl },
      }
    );
  }

  openSignatureModal(): void {
    if (this.isFormDisabled) {
      return;
    }

    const currentSignature = this.signatureForm.get('signature')?.value;

    if (
      !currentSignature ||
      currentSignature === '' ||
      currentSignature === null
    ) {
      this.isSignatureEdit = true;
      this.signatureForm.get('signature')?.enable();
    }

    try {
      this.isSignatureModalOpen = true;
    } catch (error) {
      if (this.signatureModal) {
        this.signatureModal.present();
      }
    }
  }

  closeSignatureModal(): void {
    this.isSignatureModalOpen = false;
  }

  resetSignature(): void {
    this.signatureForm.get('signature')?.setValue(null);
    this.signatureForm.reset();
  }

  public openTotalCatchesModal(): void {
    if (!this.totalCatchesData) {
      return;
    }

    this.dialog.open(TotalCatchesDialogComponent, {
      data: {
        totalCatches: this.totalCatchesData.totalCatches,
        catches: this.totalCatchesData.catches,
      },
      width: '90vw',
      maxWidth: '500px',
    });
  }

  public openAreasModulesSelection(): void {
    if (this.isFormDisabled) {
      return;
    }

    const dialogData: FollowUpAreasModulesDialogData = {
      id: this.visit.id,
      currentSelections:
        this.selectedAreasModules.length > 0
          ? this.selectedAreasModules
          : undefined,
    };

    const dialogRef = this.dialog.open(FollowUpAreasModulesDialogComponent, {
      data: dialogData,
      width: '90vw',
      maxWidth: '800px',
      disableClose: false,
    });

    dialogRef
      .afterClosed()
      .subscribe((result: FollowUpAreasModulesDialogResult) => {
        if (result) {
          this.selectedAreasModules = result.selectedAreasModules;
          this.snackbarService.open(
            'green',
            'AREA.DETAILS.VISIT_SUMMARY.MESSAGES.AREAS_MODULES_SELECTION_SUCCESS'
          );
        }
      });
  }

  public getSelectedModulesCount(): number {
    let count = 0;
    this.selectedAreasModules.forEach((area) => {
      if (area.modules) {
        count += area.modules.filter((module) => module.value === true).length;
      }
    });
    return count;
  }

  public getSelectedAreasCount(): number {
    return this.selectedAreasModules.filter((area) => {
      if (area.modules) {
        return area.modules.some((module) => module.value === true);
      }
      return false;
    }).length;
  }

  private checkNextVisitDate(weeksAhead: number): void {
    if (!weeksAhead || !this.visit.nextVisit || !this.visit.nextVisit.date) {
      this.followUpWarning = null;
      return;
    }

    const currentDate = new Date();
    const calculatedNextVisit = new Date(currentDate);
    calculatedNextVisit.setDate(currentDate.getDate() + weeksAhead);

    const existingNextVisit = new Date(this.visit.nextVisit.date);
    const diffTime = Math.abs(
      existingNextVisit.getTime() - calculatedNextVisit.getTime()
    );
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 10) {
      this.followUpWarning =
        'The calculated next visit date is less than 10 days from the existing next visit date. Please verify the follow-up period.';
    } else {
      this.followUpWarning = null;
    }
  }

  private updateFormDisabledState(): void {
    if (this.isFormDisabled) {
      this.form.disable();
      this.signatureForm.disable();
    } else {
      this.form.enable();
      this.signatureForm.enable();
    }
  }

  private updateSignatureValidation(noContactPresent: boolean): void {
    const signatureNameControl = this.form.get('signatureName');

    if (noContactPresent) {
      signatureNameControl?.clearValidators();
    } else {
      signatureNameControl?.setValidators([Validators.required]);
    }

    signatureNameControl?.updateValueAndValidity();
  }

  private updateFollowUpWeeksAheadValidation(followUpNeeded: boolean): void {
    const followUpWeeksAheadControl = this.form.get('followUpWeeksAhead');

    if (followUpNeeded) {
      followUpWeeksAheadControl?.setValidators([Validators.required]);
    } else {
      followUpWeeksAheadControl?.clearValidators();
    }

    followUpWeeksAheadControl?.updateValueAndValidity();
  }

  private updateFollowUpValidation(): void {
    const followUpNeededControl = this.form.get('followUpNeeded');

    if (this.isMaxVisitsReached) {
      followUpNeededControl?.clearValidators();
      followUpNeededControl?.setValue(null);
      followUpNeededControl?.updateValueAndValidity();
    } else {
      followUpNeededControl?.setValidators([Validators.required]);
      followUpNeededControl?.updateValueAndValidity();
    }
  }

  private updateIpmQuestionnaireValidation(): void {
    const ipmQuestionnaireControl = this.form.get(
      'ipmQuestionnaireUpdateNeeded'
    );

    if (!ipmQuestionnaireControl) {
      return;
    }

    if (this.isFormDisabled) {
      ipmQuestionnaireControl.disable();
      if (this.shouldShowIPM) {
        this.visit.questionnaireRequiredChanges = false;
        ipmQuestionnaireControl.setValidators([Validators.required]);
      } else {
        ipmQuestionnaireControl.clearValidators();
      }
      ipmQuestionnaireControl.updateValueAndValidity({ emitEvent: false });
      return;
    }

    if (
      this.visit.questionnaireRequiredChanges === true &&
      this.shouldShowIPM
    ) {
      ipmQuestionnaireControl.setValue(true);
      ipmQuestionnaireControl.disable();
      ipmQuestionnaireControl.setValidators([Validators.required]);
    } else if (this.isIPMQuestionnaireFilled === false && this.shouldShowIPM) {
      ipmQuestionnaireControl.setValue(true);
      ipmQuestionnaireControl.disable();
      ipmQuestionnaireControl.setValidators([Validators.required]);
    } else {
      ipmQuestionnaireControl.enable();

      if (this.shouldShowIPM) {
        this.visit.questionnaireRequiredChanges = false;
        ipmQuestionnaireControl.setValidators([Validators.required]);
      } else {
        ipmQuestionnaireControl.clearValidators();
      }
    }

    ipmQuestionnaireControl.updateValueAndValidity({ emitEvent: false });
  }

  private checkMonitorsCheckStatus(): void {
    if (!this.allMonitorsForVisit || this.allMonitorsForVisit.length === 0) {
      this.hasUnchangedCheckStatusMonitors = false;
      return;
    }

    // Check if any monitor with checkStatus=true has observation.id = 200 or no observation
    this.hasUnchangedCheckStatusMonitors = this.allMonitorsForVisit.some(
      (monitor) =>
        monitor.checkStatus === true &&
        (!monitor.observation?.id || monitor.observation.id === 200)
    );
  }
}
